// Geographic placement for the Decision Graph globe viewport.
//
// HONESTY RULE (same discipline as the colour-governance table): a node is placed
// at a real latitude/longitude ONLY when a legitimate coordinate exists for it.
//   • DEMO runs (data_classification: "demo") are assigned a FIXED, CURATED set of
//     real, named cities/countries — legitimate demo-scenario design, never presented
//     as real operational data.
//   • REAL (non-demo) runs carry NO per-entity geo field today, so their entities are
//     returned as `unplaced` (location unavailable). They are NEVER defaulted to a
//     fabricated or (0,0) coordinate.
//
// This module is deterministic: the same model + scenario always yields the same
// placement, so a snapshot re-renders identically.

// Curated real-city pools per demo scenario. Each entry is a genuine place with its
// real latitude/longitude. Roles: source (incident origin), agents (mobilised sites),
// candidates (alternative routes/sites), decision (accepted site).
const CURATED = {
  xius: {
    source: { name: "London, UK", lat: 51.5074, lon: -0.1278 },
    agents: [
      { name: "Frankfurt, DE", lat: 50.1109, lon: 8.6821 },
      { name: "Singapore", lat: 1.3521, lon: 103.8198 },
      { name: "São Paulo, BR", lat: -23.5505, lon: -46.6333 },
      { name: "Dubai, AE", lat: 25.2048, lon: 55.2708 },
      { name: "Mumbai, IN", lat: 19.076, lon: 72.8777 },
      { name: "Sydney, AU", lat: -33.8688, lon: 151.2093 },
      { name: "Tokyo, JP", lat: 35.6762, lon: 139.6503 },
      { name: "Johannesburg, ZA", lat: -26.2041, lon: 28.0473 },
    ],
    candidates: [
      { name: "Amsterdam, NL", lat: 52.3676, lon: 4.9041 },
      { name: "Marseille, FR", lat: 43.2965, lon: 5.3698 },
      { name: "Hong Kong", lat: 22.3193, lon: 114.1694 },
      { name: "Ashburn, US", lat: 39.0438, lon: -77.4874 },
      { name: "Fortaleza, BR", lat: -3.7319, lon: -38.5267 },
      { name: "Chennai, IN", lat: 13.0827, lon: 80.2707 },
    ],
    decision: { name: "Amsterdam, NL", lat: 52.3676, lon: 4.9041 },
  },
  griffendo: {
    source: { name: "Washington, US", lat: 38.9072, lon: -77.0369 },
    agents: [
      { name: "Austin, US", lat: 30.2672, lon: -97.7431 },
      { name: "Dublin, IE", lat: 53.3498, lon: -6.2603 },
      { name: "Bengaluru, IN", lat: 12.9716, lon: 77.5946 },
      { name: "Sydney, AU", lat: -33.8688, lon: 151.2093 },
      { name: "Toronto, CA", lat: 43.6532, lon: -79.3832 },
      { name: "Tel Aviv, IL", lat: 32.0853, lon: 34.7818 },
      { name: "Warsaw, PL", lat: 52.2297, lon: 21.0122 },
      { name: "Seoul, KR", lat: 37.5665, lon: 126.978 },
    ],
    candidates: [
      { name: "Reston, US", lat: 38.9586, lon: -77.357 },
      { name: "London, UK", lat: 51.5074, lon: -0.1278 },
      { name: "Frankfurt, DE", lat: 50.1109, lon: 8.6821 },
      { name: "Singapore", lat: 1.3521, lon: 103.8198 },
      { name: "Manila, PH", lat: 14.5995, lon: 120.9842 },
      { name: "Kraków, PL", lat: 50.0647, lon: 19.945 },
    ],
    decision: { name: "Reston, US", lat: 38.9586, lon: -77.357 },
  },
};

export function scenarioKey(model, moduleId) {
  const m = (moduleId || model?.nodes?.[0]?.label || "").toString().toLowerCase();
  if (m.includes("xius")) return "xius";
  if (m.includes("griffendo")) return "griffendo";
  return null;
}

// Resolve a placement for every node in the model.
// Returns { placed: Map<nodeId,{name,lat,lon}>, unplaced: [{id,label,type}], demo: bool }.
export function resolveGeo({ model, moduleId, isDemo }) {
  const placed = new Map();
  const unplaced = [];
  if (!model || !Array.isArray(model.nodes)) return { placed, unplaced, demo: false };

  const key = scenarioKey(model, moduleId);
  const pool = isDemo && key ? CURATED[key] : null;

  if (!pool) {
    // Real run (or unknown scenario): no honest coordinates → everything unplaced.
    model.nodes.forEach((n) => unplaced.push({ id: n.id, label: n.label, type: n.type }));
    return { placed, unplaced, demo: false };
  }

  let ai = 0;
  let ci = 0;
  model.nodes.forEach((n) => {
    if (n.type === "source") {
      placed.set(n.id, pool.source);
    } else if (n.type === "decision") {
      placed.set(n.id, pool.decision);
    } else if (n.type === "agent") {
      const p = pool.agents[ai % pool.agents.length];
      ai += 1;
      placed.set(n.id, p);
    } else if (n.type === "candidate") {
      const p = pool.candidates[ci % pool.candidates.length];
      ci += 1;
      placed.set(n.id, p);
    } else {
      unplaced.push({ id: n.id, label: n.label, type: n.type });
    }
  });
  return { placed, unplaced, demo: true };
}

// Convert latitude/longitude (degrees) to a unit-sphere position (radius r).
// Equirectangular convention: lon 0 faces +Z, +lon rotates east.
export function latLonToVec3(lat, lon, r = 1) {
  const phi = (90 - lat) * (Math.PI / 180);
  const theta = (lon + 180) * (Math.PI / 180);
  const x = -r * Math.sin(phi) * Math.cos(theta);
  const z = r * Math.sin(phi) * Math.sin(theta);
  const y = r * Math.cos(phi);
  return [x, y, z];
}

// Great-circle arc points between two lat/lon, bowed above the sphere surface.
// Returns an array of [x,y,z] on a sphere of the given base radius, with the mid
// section lifted by `lift` (fraction of radius) so the arc curves above the globe.
export function greatCircleArc(a, b, { r = 1, lift = 0.35, segments = 48 } = {}) {
  const va = latLonToVec3(a.lat, a.lon, 1);
  const vb = latLonToVec3(b.lat, b.lon, 1);
  const dot = Math.max(-1, Math.min(1, va[0] * vb[0] + va[1] * vb[1] + va[2] * vb[2]));
  const omega = Math.acos(dot);
  const sinO = Math.sin(omega) || 1e-6;
  const pts = [];
  for (let i = 0; i <= segments; i++) {
    const t = i / segments;
    const s1 = Math.sin((1 - t) * omega) / sinO;
    const s2 = Math.sin(t * omega) / sinO;
    let x = s1 * va[0] + s2 * vb[0];
    let y = s1 * va[1] + s2 * vb[1];
    let z = s1 * va[2] + s2 * vb[2];
    const len = Math.hypot(x, y, z) || 1e-6;
    x /= len; y /= len; z /= len;
    // Parabolic lift, peaking at the mid-point, scaled by arc length.
    const bow = 1 + lift * Math.sin(Math.PI * t) * (0.5 + omega / Math.PI);
    pts.push([x * r * bow, y * r * bow, z * r * bow]);
  }
  return pts;
}
